import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { api } from "../api.ts";
import { deviceName, offerDevicePasskey, remember } from "../device-passkey.tsx";
import { useMe } from "../session.ts";
import { Button, ErrorText, Field, Input, Loading, useDocumentTitle } from "../ui.tsx";

function AuthFrame({
  title,
  lead,
  children,
  footer,
}: {
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useDocumentTitle(title);
  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="font-display text-xl font-bold tracking-tight">ninekeep</span>
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-balance">{title}</h1>
        {lead && <p className="mt-1 text-muted">{lead}</p>}
        <div className="mt-6">{children}</div>
        {footer && <div className="mt-6 text-sm text-muted">{footer}</div>}
      </div>
    </main>
  );
}

/** Where to go after signing in: only a page of this app, never another site. */
const safeNext = (next: string | null) =>
  next?.startsWith("/") && !next.startsWith("//") && !next.startsWith("/\\") ? next : null;

function useSubmit(fn: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { busy, error, submit } = useSubmit(async () => {
    const r = await api.post<{ next: string }>("/auth/login", { email, password });
    await qc.invalidateQueries({ queryKey: ["me"] });
    const next = safeNext(params.get("next"));
    nav(
      r.next === "mfa_verify"
        ? `/mfa/verify${next ? `?next=${encodeURIComponent(next)}` : ""}`
        : "/mfa/setup",
    );
  });
  return (
    <AuthFrame
      title="Sign in"
      footer={
        <>
          New to ninekeep?{" "}
          <Link className="font-medium text-accent" to="/waitlist">
            Get started
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Work email">
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label="Password">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        {params.get("sso_error") && <ErrorText error={new Error(params.get("sso_error")!)} />}
        <ErrorText error={error} />
        <Button type="submit" variant="primary" busy={busy}>
          Sign in
        </Button>
        <GoogleButton next={safeNext(params.get("next"))} />
        <SsoButton email={email} />
      </form>
    </AuthFrame>
  );
}

/** "Continue with Google", shown when this ninekeep has a Google client. */
function GoogleButton({ invite, next }: { invite?: string; next?: string | null }) {
  const { data } = useQuery({
    queryKey: ["google-login"],
    queryFn: () => api.get<{ enabled: boolean }>("/auth/google/enabled"),
    staleTime: Infinity,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!data?.enabled) return null;
  return (
    <>
      <Button
        type="button"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const r = await api.post<{ url: string }>("/auth/google/start", {
              invite,
              next: next ?? undefined,
            });
            window.location.assign(r.url);
          } catch (e) {
            setError(e);
            setBusy(false);
          }
        }}
      >
        <svg aria-hidden="true" viewBox="0 0 48 48" className="size-4">
          <path
            fill="#EA4335"
            d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.2l7.8 6.1C12.4 13.6 17.7 9.5 24 9.5z"
          />
          <path
            fill="#4285F4"
            d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z"
          />
          <path
            fill="#FBBC05"
            d="M10.5 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.8l7.8-6.1z"
          />
          <path
            fill="#34A853"
            d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.8 6.1C6.6 42.6 14.6 48 24 48z"
          />
        </svg>
        Continue with Google
      </Button>
      <ErrorText error={error} />
    </>
  );
}

/** Single sign-on: the email's domain picks the organization's identity provider. */
function SsoButton({ email }: { email: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <>
      <Button
        type="button"
        busy={busy}
        disabled={!email.includes("@")}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const r = await api.post<{ url: string }>("/auth/sso/start", { email });
            window.location.assign(r.url);
          } catch (e) {
            setError(e);
            setBusy(false);
          }
        }}
      >
        Sign in with single sign-on
      </Button>
      <ErrorText error={error} />
    </>
  );
}

/** Open sign-up is closed while ninekeep is waitlist-only; invited teammates still join here. */
export function SignUp() {
  const [params] = useSearchParams();
  return params.get("invite") ? <SignUpForm /> : <Navigate to="/waitlist" replace />;
}

function SignUpForm() {
  const [params] = useSearchParams();
  const invite = params.get("invite") ?? undefined;
  // An access invite from ninekeep's team sets up a new company; a teammate's joins theirs.
  const company = params.get("company");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { busy, error, submit } = useSubmit(async () => {
    await api.post("/auth/signup", { name, email, password, invite });
    await qc.invalidateQueries({ queryKey: ["me"] });
    nav("/mfa/setup");
  });
  return (
    <AuthFrame
      title={
        company
          ? `Set up ${company} on ninekeep`
          : invite
            ? "Join your team on ninekeep"
            : "Find the money your vendors owe you"
      }
      lead={
        invite && !company
          ? undefined
          : "Create an account, drop in your vendor contracts, and see what you were owed in the last 12 months."
      }
      footer={
        <>
          Already have an account?{" "}
          <Link className="font-medium text-accent" to="/signin">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Your name">
          {(id) => (
            <Input
              id={id}
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field
          label="Work email"
          hint={invite ? undefined : "Your company name comes from your email's domain."}
        >
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              readOnly={!!invite}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label="Password" hint="At least 12 characters.">
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <ErrorText error={error} />
        <Button type="submit" variant="primary" busy={busy}>
          Create account
        </Button>
        <GoogleButton invite={invite} />
        <p className="text-xs text-muted">
          ninekeep prepares claims for you to review. It is not a law firm, and your contracts are
          never used to train models.
        </p>
      </form>
    </AuthFrame>
  );
}

export function MfaSetup() {
  const { data: me, isLoading } = useMe();
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [startError, setStartError] = useState<unknown>(null);
  const started = useRef(false);
  const nav = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    if (!me?.user || me.mfa.enabled || started.current) return;
    // Starting again would replace the secret on screen, so only once per visit.
    started.current = true;
    api
      .post<{ secret: string; uri: string }>("/auth/mfa/setup")
      .then(async (s) => {
        setSetup(s);
        setQr(await QRCode.toDataURL(s.uri, { margin: 1, width: 200 }));
      })
      .catch(setStartError);
  }, [me]);

  const { busy, error, submit } = useSubmit(async () => {
    const r = await api.post<{ backupCodes: string[] }>("/auth/mfa/enable", { code });
    setCodes(r.backupCodes);
  });

  if (isLoading) return <Loading />;
  if (!me?.user)
    return (
      <AuthFrame title="Sign in first">
        <Link className="text-accent" to="/signin">
          Sign in
        </Link>
      </AuthFrame>
    );
  if (me.mfa.enabled && !codes)
    return (
      <AuthFrame title="Two-factor is on">
        <Link className="text-accent" to="/">
          Continue
        </Link>
      </AuthFrame>
    );

  if (codes)
    return (
      <AuthFrame
        title="Save your backup codes"
        lead="Each code works once if you lose your authenticator. Store them somewhere safe."
      >
        <div className="tabular grid grid-cols-2 gap-2 rounded-control border border-line bg-surface p-4 font-mono text-sm">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <Button
          className="mt-6 w-full"
          variant="primary"
          onClick={async () => {
            await qc.invalidateQueries({ queryKey: ["me"] });
            nav("/");
          }}
        >
          I've saved them
        </Button>
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="Set up two-factor authentication"
      lead="ninekeep requires it for everyone. Scan this code with an authenticator app, then enter the 6-digit code it shows."
    >
      <ErrorText error={startError} />
      {setup && (
        <div className="flex flex-col items-center gap-3 rounded-control border border-line bg-surface p-4">
          {qr && (
            <img
              src={qr}
              alt="QR code for your authenticator app"
              className="size-[200px] rounded-chip bg-white p-1"
            />
          )}
          <p className="text-xs text-muted">Or enter this key:</p>
          <code className="text-xs break-all">{setup.secret}</code>
        </div>
      )}
      <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
        <Field label="Code from your app">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          )}
        </Field>
        <ErrorText error={error} />
        <Button type="submit" variant="primary" busy={busy} disabled={!setup}>
          Turn on two-factor
        </Button>
      </form>
      <div className="mt-6 border-t border-line pt-6">
        <p className="mb-3 text-sm text-muted">
          Or use a passkey: your device's fingerprint, face or PIN.
        </p>
        <PasskeyButton
          mode="register"
          label="Set up a passkey instead"
          onDone={async () => {
            await qc.invalidateQueries({ queryKey: ["me"] });
            nav("/");
          }}
        />
      </div>
    </AuthFrame>
  );
}

/** Registers a passkey, or uses one as the second factor, with the browser's WebAuthn prompt. */
export function PasskeyButton({
  mode,
  label,
  onDone,
}: {
  mode: "register" | "authenticate";
  label: string;
  onDone: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            if (mode === "register") {
              const optionsJSON =
                await api.post<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
                  "/auth/passkeys/options",
                );
              const response = await startRegistration({ optionsJSON });
              await api.post("/auth/passkeys", { response, name: deviceName() });
              remember("added");
            } else {
              const optionsJSON = await api.post<
                Parameters<typeof startAuthentication>[0]["optionsJSON"]
              >("/auth/mfa/passkey/options");
              const response = await startAuthentication({ optionsJSON });
              await api.post("/auth/mfa/passkey", { response });
            }
            await onDone();
          } catch (e) {
            setError(
              e instanceof Error && e.name === "NotAllowedError"
                ? new Error("The passkey window was closed. Try again.")
                : e,
            );
          } finally {
            setBusy(false);
          }
        }}
      >
        {label}
      </Button>
      <ErrorText error={error} />
    </div>
  );
}

export function MfaVerify() {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { busy, error, submit } = useSubmit(async () => {
    await api.post("/auth/mfa/verify", backup ? { backupCode: code } : { code });
    await qc.invalidateQueries({ queryKey: ["me"] });
    nav(safeNext(params.get("next")) ?? "/");
    // Signed in with a code on this device: offer one tap here next time.
    void offerDevicePasskey();
  });
  return (
    <AuthFrame
      title="Enter your two-factor code"
      lead={
        backup
          ? "Enter one of your backup codes."
          : "Open your authenticator app and enter the 6-digit code for ninekeep."
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={backup ? "Backup code" : "Code"}>
          {(id) => (
            <Input
              id={id}
              autoFocus
              inputMode={backup ? "text" : "numeric"}
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
            />
          )}
        </Field>
        <ErrorText error={error} />
        <Button type="submit" variant="primary" busy={busy}>
          Continue
        </Button>
        <button
          type="button"
          className="text-left text-sm text-accent"
          onClick={() => setBackup((b) => !b)}
        >
          {backup ? "Use your authenticator app" : "Use a backup code"}
        </button>
        <PasskeyButton
          mode="authenticate"
          label="Use a passkey"
          onDone={async () => {
            await qc.invalidateQueries({ queryKey: ["me"] });
            nav(safeNext(params.get("next")) ?? "/");
          }}
        />
      </form>
    </AuthFrame>
  );
}
